
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonSpinner,
    IonIcon,
    IonButtons,
    IonButton,
    IonInput,
    IonSelect,
    IonSelectOption,
    menuController,
    alertController,
} from "@ionic/vue";
import { refresh, menu, checkmark, close } from "ionicons/icons";

import { computed, ref } from "vue";
import { Calendar } from "v-calendar";

import moment from "moment";
import "moment/locale/it";

import { LOCALSTORAGE_KEY } from "@/custom_config";

import { openToast } from "@/services/toast";
import apiResponsabile from "@/services/responsabile";
import apiImpostazioniHr from "@/services/impostazioni_hr";

export default {
    name: "RiepilogoRichieste",
    components: {
        IonHeader,
        IonToolbar,
        IonTitle,
        IonContent,
        IonPage,
        IonSpinner,
        IonIcon,
        IonButtons,
        IonButton,
        IonInput,
        IonSelect,
        IonSelectOption,
        Calendar,
    },
    setup() {
        const dipendenteID = JSON.parse(localStorage.getItem(`${LOCALSTORAGE_KEY}`)).dipendenti_id;

        const loading = ref(false);
        const processing = ref(false);
        const richieste = ref([]);
        const searchQuery = ref("");
        const selectedTipologiaFilter = ref("");
        const selectedStatoFilter = ref("");
        const calendarMonth = ref(new Date());
        const selectedCalendarDay = ref(null);
        const showPermessoDateDiverse = ref(false);

        const dataRichiesta = computed(() => {
            return (dataInizio, dataFine) => {
                const inizio = moment(dataInizio).format("DD/MM/YYYY");
                const fine = moment(dataFine).format("DD/MM/YYYY");
                if (!moment(dataFine).isValid()) {
                    return `Dal ${inizio}`;
                } else if (inizio == fine) {
                    return `${inizio}`;
                } else {
                    return `Dal ${inizio} al ${fine}`;
                }
            };
        });

        function isGiornataInteraRichiesta(richiesta) {
            const v = richiesta.richieste_tipologia_giornata_intera;
            return v == 1 || v === "1";
        }

        const orarioRichiesta = computed(() => {
            const permessoDateDiverse = showPermessoDateDiverse.value;
            return (richiesta) => {
                const tipo = String(richiesta.richieste_tipologia);
                if (tipo === "2" || tipo === "3") {
                    return;
                }
                const giornataIntera = isGiornataInteraRichiesta(richiesta);
                let mostraOrario = !giornataIntera;
                if (tipo === "1" && permessoDateDiverse) {
                    const dal = richiesta.richieste_dal;
                    const al = richiesta.richieste_al;
                    const alValid = al && moment(al).isValid();
                    const stessaGiornata =
                        dal &&
                        alValid &&
                        moment(dal).format("YYYY-MM-DD") === moment(al).format("YYYY-MM-DD");
                    if (alValid && !stessaGiornata) {
                        mostraOrario = false;
                    }
                }
                if (
                    mostraOrario &&
                    richiesta.richieste_ora_inizio &&
                    richiesta.richieste_ora_fine
                ) {
                    return `, ${richiesta.richieste_ora_inizio} - ${richiesta.richieste_ora_fine}`;
                }
            };
        });

        function isRichiestaGestibile(richiesta) {
            const stato = richiesta.richieste_stato_id != null ? richiesta.richieste_stato_id : richiesta.richieste_stato;
            return String(stato) === "1";
        }

        function isValidDate(value) {
            return value && moment(value).isValid();
        }

        function getRichiesteTouchingDate(targetDate) {
            const dayStart = moment(targetDate).startOf("day");
            const dayEnd = moment(targetDate).endOf("day");
            return richieste.value.filter((richiesta) => {
                const dal = moment(richiesta.richieste_dal).startOf("day");
                const al = isValidDate(richiesta.richieste_al)
                    ? moment(richiesta.richieste_al).endOf("day")
                    : dal.clone().endOf("day");
                return dal.isSameOrBefore(dayEnd) && al.isSameOrAfter(dayStart);
            });
        }

        function buildDayPresenceMap(list) {
            const dayMap = {};
            list.forEach((richiesta) => {
                const start = moment(richiesta.richieste_dal).startOf("day");
                const end = isValidDate(richiesta.richieste_al)
                    ? moment(richiesta.richieste_al).startOf("day")
                    : start.clone();
                for (let d = start.clone(); d.isSameOrBefore(end); d.add(1, "day")) {
                    const key = d.format("YYYY-MM-DD");
                    dayMap[key] = true;
                }
            });
            return dayMap;
        }

        const calendarAttributes = computed(() => {
            const todayStr = moment().format("YYYY-MM-DD");
            const dayMap = buildDayPresenceMap(richieste.value);
            const attrs = Object.keys(dayMap)
                .sort()
                .map((dayStr) => {
                    return {
                        key: `ri-${dayStr}`,
                        dates: moment(dayStr, "YYYY-MM-DD").toDate(),
                        highlight: { color: "blue", fillMode: "solid" },
                    };
                });
            if (!dayMap[todayStr]) {
                attrs.push({
                    key: `ri-oggi-${todayStr}`,
                    dates: moment(todayStr, "YYYY-MM-DD").toDate(),
                    highlight: { color: "blue", fillMode: "solid" },
                });
            }
            return attrs;
        });

        const dayClicked = (day) => {
            selectedCalendarDay.value = day.id;
        };

        function clearCalendarDayFilter() {
            selectedCalendarDay.value = null;
        }

        const calendarSelectedLabel = computed(() => {
            if (!selectedCalendarDay.value) {
                return "";
            }
            return moment(selectedCalendarDay.value).locale("it").format("dddd D MMMM YYYY");
        });

        const tipologiaFilterOptions = computed(() => {
            const map = {};
            richieste.value.forEach((richiesta) => {
                const idRaw =
                    richiesta.richieste_tipologia_id != null
                        ? richiesta.richieste_tipologia_id
                        : richiesta.richieste_tipologia;
                const id = String(idRaw);
                if (!map[id]) {
                    map[id] = richiesta.richieste_tipologia_value || `Tipologia ${id}`;
                }
            });
            return Object.keys(map)
                .sort((a, b) => Number(a) - Number(b))
                .map((id) => ({ id, label: map[id] }));
        });

        const statoFilterOptions = computed(() => {
            const map = {};
            richieste.value.forEach((richiesta) => {
                const idRaw =
                    richiesta.richieste_stato_id != null
                        ? richiesta.richieste_stato_id
                        : richiesta.richieste_stato;
                const id = String(idRaw);
                if (!map[id]) {
                    map[id] = richiesta.richieste_stato_value || `Stato ${id}`;
                }
            });
            return Object.keys(map)
                .sort((a, b) => Number(a) - Number(b))
                .map((id) => ({ id, label: map[id] }));
        });

        const openMenu = () => {
            menuController.open("app-menu");
        };

        /**
         * Load all pending requests for approval
         */
        async function loadRichieste() {
            loading.value = true;
            try {
                const response = await apiResponsabile.getRichieste(dipendenteID);
                console.log(response);
                if(response.status === 200 && response.data.status === 0) {
                    richieste.value = response.data.data;
                } else {
                    openToast("Errore durante il caricamento delle richieste", "toast_danger");
                    richieste.value = [];
                }
            } catch (error) {
                console.error(error);
                openToast("Errore durante il caricamento delle richieste", "toast_danger");
                richieste.value = [];
            }
            loading.value = false;
        }
        loadRichieste();

        async function loadImpostazioniHr() {
            try {
                const res = await apiImpostazioniHr.getImpostazioniHr();
                if (res.status === 200 && res.data.status === 0 && res.data.data.length > 0) {
                    const settings = res.data.data[0];
                    if (settings.impostazioni_hr_permesso_date_diverse !== undefined) {
                        showPermessoDateDiverse.value =
                            settings.impostazioni_hr_permesso_date_diverse == "1";
                    } else {
                        showPermessoDateDiverse.value = false;
                    }
                }
            } catch (error) {
                console.error(error);
            }
        }
        loadImpostazioniHr();

        /**
         * Filter requests based on search query
         */
        const richiesteToShow = computed(() => {
            let list = richieste.value;
            if (selectedCalendarDay.value) {
                list = getRichiesteTouchingDate(selectedCalendarDay.value);
            }
            if (selectedTipologiaFilter.value) {
                list = list.filter((richiesta) => {
                    const idRaw =
                        richiesta.richieste_tipologia_id != null
                            ? richiesta.richieste_tipologia_id
                            : richiesta.richieste_tipologia;
                    return String(idRaw) === selectedTipologiaFilter.value;
                });
            }
            if (selectedStatoFilter.value) {
                list = list.filter((richiesta) => {
                    const idRaw =
                        richiesta.richieste_stato_id != null
                            ? richiesta.richieste_stato_id
                            : richiesta.richieste_stato;
                    return String(idRaw) === selectedStatoFilter.value;
                });
            }
            return list.filter((richiesta) => {
                const searchTerm = searchQuery.value.toLowerCase();
                if (!searchTerm) {
                    return true;
                }

                const tipologiaMatch = richiesta.richieste_tipologia_value && 
                    richiesta.richieste_tipologia_value.toLowerCase().includes(searchTerm);
                const nomeMatch = richiesta.dipendenti_nome && 
                    richiesta.dipendenti_nome.toLowerCase().includes(searchTerm);
                const cognomeMatch = richiesta.dipendenti_cognome && 
                    richiesta.dipendenti_cognome.toLowerCase().includes(searchTerm);
                const noteMatch = richiesta.richieste_note && 
                    richiesta.richieste_note.toLowerCase().includes(searchTerm);
                
                return tipologiaMatch || nomeMatch || cognomeMatch || noteMatch;
            });
        });

        /**
         * Approve a request
         */
        async function approveRichiesta(richiestaId: string) {
            const alert = await alertController.create({
                header: 'Conferma approvazione',
                message: 'Sei sicuro di voler approvare questa richiesta?',
                buttons: [
                    {
                        text: 'Annulla',
                        role: 'cancel'
                    },
                    {
                        text: 'Approva',
                        handler: async () => {
                            await setStatoRichiesta(richiestaId, '2');
                        }
                    }
                ]
            });
            await alert.present();
        }

        /**
         * Reject a request
         */
        async function rejectRichiesta(richiestaId: string) {
            const alert = await alertController.create({
                header: 'Conferma rifiuto',
                message: 'Sei sicuro di voler rifiutare questa richiesta?',
                buttons: [
                    {
                        text: 'Annulla',
                        role: 'cancel'
                    },
                    {
                        text: 'Rifiuta',
                        handler: async () => {
                            await setStatoRichiesta(richiestaId, '3');
                        }
                    }
                ]
            });
            await alert.present();
        }

        /**
         * Set request status (approve or reject)
         */
        async function setStatoRichiesta(richiestaId: string, stato: string) {
            processing.value = true;
            try {
                await apiResponsabile.setStato(richiestaId, stato);
                const statoText = stato === '2' ? 'approvata' : 'rifiutata';
                openToast(`Richiesta ${statoText} con successo`, "toast_success");
                
                // Remove the request from the local array instead of reloading
                richieste.value = richieste.value.filter(richiesta => richiesta.richieste_id !== richiestaId);
            } catch (error) {
                console.error(error);
                openToast("Errore durante l'aggiornamento della richiesta", "toast_danger");
            }
            processing.value = false;
        }

        return {
            loading,
            processing,
            refresh,
            menu,
            checkmark,
            close,
            richieste,
            richiesteToShow,
            searchQuery,
            selectedTipologiaFilter,
            selectedStatoFilter,
            tipologiaFilterOptions,
            statoFilterOptions,
            dataRichiesta,
            orarioRichiesta,
            loadRichieste,
            openMenu,
            approveRichiesta,
            rejectRichiesta,
            isRichiestaGestibile,
            calendarMonth,
            calendarAttributes,
            dayClicked,
            selectedCalendarDay,
            clearCalendarDayFilter,
            calendarSelectedLabel,
        };
    },
};
